import type { ClassificationContent } from '@classification-testing/domain';
import { ContentType, ClassificationInputTab } from '@classification-testing/domain';
import { setClassificationError } from '@classification-testing/state/actions';
import { selectFileUploading } from '@classification-testing/state/selectors';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { Card, InlineTabs } from '@soteria/design-system-ve';
import {
  useClassifyContent,
  useAudioRecording,
  useFileUpload,
  useClassificationActions,
  useContentHandlers,
  useTabManagement,
} from '@soteria-ui/src/classification-testing/hooks';
import { useAppDispatch, useAppSelector } from '@store';
import { useState, useRef, useEffect, useCallback } from 'react';

import { ActionButtons } from '../../components/ActionButtons';
import { ErrorDisplay } from '../../components/ErrorDisplay';
import { TabContent } from '../../components/TabContent';

import * as styles from './ClassificationContentInputSection.css';

interface ClassificationContentInputSectionProps {
  communityId?: string;
  promptVersion?: string;
}

/**
 * ClassificationContentInputSection
 */
function ClassificationContentInputSectionComponent({ communityId, promptVersion }: ClassificationContentInputSectionProps) {
  const dispatch = useAppDispatch();
  const [content, setContent] = useState<ClassificationContent | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [activeTab, setActiveTab] = useState<ClassificationInputTab>(ClassificationInputTab.TEXT);

  const { classifyContent, isLoading, errors } = useClassifyContent(communityId, promptVersion);
  const clearError = useCallback(() => dispatch(setClassificationError(null)), [dispatch]);
  const onContentChange = useCallback((newContent: ClassificationContent) => setContent(newContent), []);

  // Use tab management hook
  const {
    tabs,
    tabValues,
    activeTabIndex,
    handleTabChange: onTabChange,
  } = useTabManagement({
    activeTab,
    onContentChange,
    setPendingFile,
    clearError,
  });

  const handleTabChange = (tabIndex: number) => {
    const method = tabValues[tabIndex];

    if (method) {
      setActiveTab(method);
      onTabChange(tabIndex);
    }
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  // Initialize audio recording functionality
  const { isRecording, startRecording, stopRecording } = useAudioRecording({
    onContentChange: content => {
      onContentChange(content);
      // Clear errors when audio content changes
      clearError();
    },
    onFileReady: file => {
      setPendingFile(file);
      // Clear errors when audio file is ready
      clearError();
    },
  });

  // Initialize file upload functionality
  const { handleFileSelect, handleFileInputChange, handlePaste } = useFileUpload({
    onContentChange: content => {
      onContentChange(content);
      // Clear errors when file content changes
      clearError();
    },
    onFileReady: file => {
      setPendingFile(file);
      // Clear errors when file is ready
      clearError();
    },
  });

  // Get file uploading state from Redux
  const isUploading = useAppSelector(selectFileUploading);

  // Use custom hook for content handlers
  const { handleTextChange, handleImageUrlChange, handleUsernameChange } = useContentHandlers({ onContentChange, clearError });

  // Handle clear button click directly
  const handleClear = useCallback(() => {
    setContent({
      type: ContentType.TEXT,
      text: '',
    });
    setPendingFile(null);
    clearError();
  }, [clearError]);

  // Use custom hook for classification actions
  const { handleClassify, hasContent } = useClassificationActions({
    communityId,
    content,
    pendingFile,
    classifyContent,
    setContent,
    setPendingFile,
    handleClear,
  });

  // Add keyboard shortcut for Ctrl+Enter to trigger classification
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === 'Enter') {
        event.preventDefault();
        if (hasContent && !isLoading) {
          handleClassify();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [hasContent, isLoading, handleClassify]);

  return (
    <Card className={styles.section}>
      <div className={styles.container}>
        <div className={styles.tabNavigation}>
          <InlineTabs aria-label="Content input tabs" size="medium" tabs={tabs} value={activeTabIndex} variant="default" onValueChange={handleTabChange} />
        </div>

        {/* Content Area */}
        <div className={styles.contentArea}>
          <TabContent
            activeTab={activeTab}
            content={content}
            errors={errors}
            fileInputRef={fileInputRef}
            isRecording={isRecording}
            isUploading={isUploading}
            pendingFile={pendingFile}
            textAreaRef={textAreaRef}
            onClear={handleClear}
            onFileInputChange={handleFileInputChange}
            onFileSelect={handleFileSelect}
            onImageUrlChange={handleImageUrlChange}
            onPaste={handlePaste}
            onStartRecording={startRecording}
            onStopRecording={stopRecording}
            onTextChange={handleTextChange}
            onUsernameChange={handleUsernameChange}
          />
        </div>
      </div>

      {/* Action Section - at bottom of card */}
      <div className={styles.actionSection}>
        <div className={styles.actionLayout}>
          <ErrorDisplay errors={errors} />
          <ActionButtons hasContent={hasContent} isLoading={isLoading} onClassify={handleClassify} />
        </div>
      </div>
    </Card>
  );
}

export const ClassificationContentInputSection = withAppInsightsTracking(ClassificationContentInputSectionComponent, 'ClassificationContentInputSection');
